import { useCallback, useMemo, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridReadyEvent,
  GridApi,
  ICellRendererParams,
  IDatasource,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Pencil, Trash2, RotateCw } from 'lucide-react';
import styles from './UserManagement.module.css';
import { localeText } from '../../../constants/AgGridLocale';
import { useStore } from '../../../hooks/useStore';
import { observer } from 'mobx-react-lite';
import { IUserManagementResult } from '../../../models/user-management/UserManagementModel';
import EditUserModal from './components/EditUserModal';
import DeleteUserDialog from './components/DeleteUserDialog';
import CommonButton from '../../../components/common-components/button/CommonButton';

const ActionCellRenderer = (props: ICellRendererParams<IUserManagementResult>) => {
  const { popupStore, userManagementStore } = useStore();

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      userManagementStore.setSelectedUser(props.data);
      popupStore.setIsEditUserOpen(true);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      userManagementStore.setSelectedUser(props.data);
      popupStore.setIsDeleteUserConfirmationOpen(true);
    }
  };

  return (
    <div className={styles.actionCell}>
      <button onClick={handleEdit} className={styles.editButton} title="Düzenle">
        <Pencil size={20} />
      </button>
      <button onClick={handleDelete} className={styles.deleteButton} title="Sil">
        <Trash2 size={20} />
      </button>
    </div>
  );
};

const UserManagement = () => {
  const [gridApi, setGridApi] = useState<GridApi<IUserManagementResult> | null>(null);
  const { popupStore, userManagementStore, uiStore } = useStore();

  const dataSource = useMemo((): IDatasource => {
    return {
      getRows: async params => {
        try {
          uiStore.setLoading(true);
          const result = await userManagementStore.listUsers({
            startRow: params.startRow,
            endRow: params.endRow,
            filterModel: params.filterModel,
            sortModel: params.sortModel,
          });
          if (result) {
            params.successCallback(result.rows, result.lastRow);
          } else {
            params.failCallback();
          }
        } catch (error) {
          console.error('Data loading error:', error);
          params.failCallback();
        } finally {
          uiStore.setLoading(false);
        }
      },
    };
  }, [userManagementStore, uiStore]);

  const onGridReady = useCallback(
    (params: GridReadyEvent<IUserManagementResult>) => {
      setGridApi(params.api);
      userManagementStore.setGridApi(params.api);
    },
    [userManagementStore]
  );
  const columnDefs = useMemo<ColDef<IUserManagementResult>[]>(
    () => [
      {
        headerName: 'İşlemler',
        width: 120,
        pinned: 'left',
        sortable: false,
        filter: false,
        cellRenderer: ActionCellRenderer,
        cellStyle: () => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          backgroundColor: 'transparent',
        }),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 100,
        filter: false,
      },
      {
        field: 'userName',
        headerName: 'Kullanıcı Adı',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'email',
        headerName: 'E-posta',
        minWidth: 200,
        flex: 1.5,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'roles',
        headerName: 'Roller',
        minWidth: 150,
        flex: 1,
        filter: false,
        valueFormatter: (params: any) => {
          return params.value ? params.value.join(', ') : '';
        },
      },
      {
        field: 'isActive',
        headerName: 'Aktif Mi?',
        minWidth: 120,
        flex: 1,
        filter: false,
        valueFormatter: (params: any) => {
          return params.value ? 'Evet' : 'Hayır';
        },
      },
      {
        field: 'createdAt',
        headerName: 'Oluşturulma Tarihi',
        minWidth: 160,
        filter: false,
        valueFormatter: (params: any) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleString('tr-TR');
        },
      },
    ],
    []
  );
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
        maxNumConditions: 1,
      },
    }),
    []
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>Kullanıcı Yönetimi</h2>
        <CommonButton
          type="secondary"
          onClick={() => userManagementStore.refreshGrid()}
          leftIcon={<RotateCw size={18} />}
        >
          Yenile
        </CommonButton>
      </div>

      <div className={styles.gridContainer}>
        <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
          <AgGridReact
            rowModelType={'infinite'}
            cacheBlockSize={20}
            maxBlocksInCache={1}
            datasource={dataSource}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={20}
            rowSelection="multiple"
            rowHeight={48}
            headerHeight={48}
            animateRows={true}
            suppressColumnVirtualisation={true}
            suppressRowVirtualisation={true}
            localeText={localeText}
          />
        </div>
      </div>

      <EditUserModal
        open={popupStore.isEditUserOpen}
        onClose={() => popupStore.setIsEditUserOpen(false)}
      />
      <DeleteUserDialog />
    </div>
  );
};

export default observer(UserManagement);
